import React, { useEffect, useMemo } from 'react';
import { AgGridReact } from '@ag-grid-community/react';
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { ClipboardModule } from "@ag-grid-enterprise/clipboard";

import { gridModeUrlBuilderPropTypes } from './gridModeUrlBuilder/propTypes';
import { ROW_COUNT_LIMIT, GRID_URL_BUILDER_ID, HEADER_HEIGHT, defaultColDef } from './gridModeUrlBuilder/constants';
import { getStaticColumnDefs, getColumnDef, columnTypes } from './gridModeUrlBuilder/columnDefinitions';
import { useGridState } from './gridModeUrlBuilder/useGridState';
import { useFields } from './conventionFields/useFields';
import FormData from './gridModeUrlBuilder/FormData';
import ColumnHeader from './gridModeUrlBuilder/ColumnHeader';

import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-balham.css';

export default function GridModeUrlBuilder({
  url_columns,
  field_columns,
  other_columns,
  initial_row_data,
  initial_pinned_row_data,
}) {
  const allColumns = useMemo(() => [...url_columns, ...field_columns, ...other_columns],
    [url_columns, field_columns, other_columns]);

  const { fields, updateFieldValue } = useFields(field_columns);

  const {
    gridApi,
    gridColumnApi,
    rowData,
    setRowData,
    pinnedTopRowData,
    setPinnedTopRowData,
    rowNodes,
    dataValid,
    saveClicked,
    setSaveClicked,
    onGridReady,
    addRow,
    deleteRows,
    duplicateRows,
    generateCombinations,
    onCellValueChanged,
    getFormatControlledValue,
    newEmptyRow,
    initialPinnedTopRowData
  } = useGridState(allColumns, updateFieldValue);

  // Initialize data
  useEffect(() => {
    const initialData = initial_row_data.length === 0 ? [newEmptyRow()] : initial_row_data;
    const initialPinned = initialPinnedTopRowData(initial_pinned_row_data);

    setRowData(initialData);
    setPinnedTopRowData(initialPinned);
  }, [initial_row_data, initial_pinned_row_data, newEmptyRow, initialPinnedTopRowData, setRowData, setPinnedTopRowData]);

  const dynamicColumnDefs = useMemo(() =>
    [...url_columns, ...fields, ...other_columns].map(getColumnDef),
    [url_columns, fields, other_columns]
  );

  const columnsCustomData = useMemo(() => {
    const map = new Map();
    [...url_columns, ...fields, ...other_columns].forEach(column => {
      map.set(column.field, column.custom_data || {});
    });
    return map;
  }, [url_columns, fields, other_columns]);

  const columnDefs = useMemo(() =>
    [...getStaticColumnDefs(), ...dynamicColumnDefs],
    [dynamicColumnDefs]
  );

  const handleSubmit = (e) => {
    setTimeout(() => {
      setSaveClicked(true);
    }, 0);
  };

  const rowCountLimitExceeded = rowNodes.length > ROW_COUNT_LIMIT;
  const saveDisabled = !dataValid || saveClicked || rowNodes.length === 0 || rowCountLimitExceeded;

  return (
    <div id={GRID_URL_BUILDER_ID}>
      <div className="mb-20">
        <a className='btn border-slate text-slate-800 btn-flat btn-xs mr-10' onClick={addRow}>Add row</a>
        <a className='btn border-danger text-danger btn-flat btn-xs mr-10' onClick={deleteRows}>Delete rows</a>
        <a className='btn border-info text-info btn-flat btn-xs mr-10' onClick={duplicateRows}>Duplicate rows</a>
        <a className='btn alpha-blue btn-xs mr-10' onClick={generateCombinations}>Generate</a>
      </div>

      <div className="ag-theme-balham" style={{ height: '100%', width: '100%' }}>
        <AgGridReact
          modules={[ClientSideRowModelModule, ClipboardModule]}
          components={{
            agColumnHeader: ColumnHeader
          }}
          rowData={rowData}
          pinnedTopRowData={pinnedTopRowData}
          columnDefs={columnDefs}
          columnTypes={columnTypes}
          onGridReady={onGridReady}
          headerHeight={HEADER_HEIGHT}
          domLayout={'autoHeight'}
          defaultColDef={{
            ...defaultColDef,
            tooltipComponent: 'agTooltipComponent'
          }}
          animateRows={false}  // Explicitly disable row animation (AG Grid 31 has it enabled by default)
          rowGroupPanelShow={'never'}
          suppressMenuHide={true}
          rowSelection={'multiple'}
          checkboxSelection={true}
          suppressRowClickSelection={true}
          processCellForClipboard={params => params.value && params.value.value || ''}
          processCellFromClipboard={params => {
            // Column-level processCellFromClipboard should take precedence if defined
            const colDef = params.column.getColDef();
            if (colDef.processCellFromClipboard) {
              return colDef.processCellFromClipboard(params);
            }

            // Default: Handle pasted text by wrapping it in the expected object format
            return typeof params.value === 'string' ? { value: params.value } : params.value;
          }}
          onCellValueChanged={onCellValueChanged}
          popupParent={document.querySelector('body')}
          tooltipShowDelay={0}
          suppressContextMenu={true}
          context={{ getFormatControlledValue, columnsCustomData }}
          suppressChangeDetection={true}
        />
      </div>

      {gridApi && gridColumnApi && <FormData gridApi={gridApi} columns={allColumns} />}

      {!dataValid && <div className='text-danger mt-20'>Please fill in all required fields</div>}
      {rowCountLimitExceeded && <div className="text-danger mt-20">You can build a maximum of {ROW_COUNT_LIMIT} URLs at one time</div>}
      <div className='text-muted text-size-small mt-20'>{rowNodes.length} URLs will be created</div>

      <div className="mt-20">
        <button
          type="submit"
          disabled={saveDisabled}
          onClick={handleSubmit}
          className="btn btn-primary">
          Save
        </button>
      </div>
    </div>
  );
}

GridModeUrlBuilder.propTypes = gridModeUrlBuilderPropTypes;